import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { Provider } from "@redotech/redo-order-common/provider";
import { AddressCollection } from "@redotech/redo-return-core-common/address-collection";
import {
  SPECIAL_RESTOCK_ID,
  Step,
} from "@redotech/redo-return-core-common/return-flow";
import { SPECIAL_LOCATION_ID } from "@redotech/redo-return-ui/flows/return-flow/set-return-locations";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo, useState } from "react";
import { LocationInfo } from "../shipping/types";
import { AddLocationModal } from "./add-location-modal";
import * as styles from "./set-return-locations.module.css";

const ADD_NEW_LOCATION_ID = "__add_new_location__";

export const LocationCard = memo(function LocationCard({
  addressId,
  index,
  resolvedAddresses,
  locationOptions,
  externalLocationId,
  restockExternalLocationId,
  onDelete,
  onLocationSelect,
  onRestockLocationSelect,
  selectedLocations,
  cardTitle,
}: {
  addressId: string;
  index: number;
  resolvedAddresses: AddressCollection[];
  locationOptions: LocationInfo[];
  externalLocationId?: string;
  restockExternalLocationId?: string | null;
  onDelete: (addressId: string) => void;
  onLocationSelect: (addressId: string, externalLocationId?: string) => void;
  onRestockLocationSelect: (
    addressId: string,
    restockLocationId: string | null,
  ) => void;
  selectedLocations: string[];
  cardTitle?: string;
}) {
  const { t } = useTranslation();
  const team = useContext(TeamContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const [modalOpen, setModalOpen] = useState(false);
  const isShopify = locationOptions.some(
    (location) => location.isExternal && location.provider === Provider.SHOPIFY,
  );
  const isRedoManaged = addressId === SPECIAL_LOCATION_ID.REDO_MANAGED;

  const [externalLocationMap, setExternalLocationMap] = useState<
    Map<string, string>
  >(new Map());

  const locationDropdownOptions = useMemo<RedoListItem<string>[]>(() => {
    const selectedExternalIds = new Set<string>();
    for (const selectedId of selectedLocations) {
      const resolvedAddr = resolvedAddresses.find(
        (addr) => addr._id.toString() === selectedId,
      );
      if (resolvedAddr?.externalId) {
        selectedExternalIds.add(resolvedAddr.externalId);
      } else {
        selectedExternalIds.add(selectedId);
      }
    }

    const options: RedoListItem<string>[] = locationOptions
      .filter(
        (loc) =>
          // Include if it's the current selection
          (externalLocationId
            ? loc.id === externalLocationId
            : loc.id === addressId) ||
          // OR if it's not already selected (check by external ID)
          !selectedExternalIds.has(loc.id),
      )
      .map((loc) => ({
        type: "text" as const,
        id: loc.id,
        text: loc.name,
        value: loc.id,
      }));

    options.push({
      type: "text" as const,
      id: ADD_NEW_LOCATION_ID,
      text: t("Add new custom location"),
      leadingItem: { type: "icon", Icon: PlusIcon },
      value: ADD_NEW_LOCATION_ID,
    });

    return options;
  }, [
    addressId,
    locationOptions,
    externalLocationId,
    selectedLocations,
    resolvedAddresses,
    t,
  ]);

  const selectedLocationItem = useMemo(() => {
    if (externalLocationId) {
      return locationDropdownOptions.find(
        (opt) => opt.id === externalLocationId,
      );
    }

    return locationDropdownOptions.find((opt) => opt.id === addressId);
  }, [locationDropdownOptions, addressId, externalLocationId]);

  const restockDropdownOptions = useMemo<RedoListItem<string>[]>(() => {
    const options: RedoListItem<string>[] = [];

    options.push({
      type: "text" as const,
      id: SPECIAL_RESTOCK_ID.BACK_TO_FULFILLMENT,
      text: t("Back to fulfillment location"),
      value: SPECIAL_RESTOCK_ID.BACK_TO_FULFILLMENT,
    });

    options.push({
      type: "text" as const,
      id: SPECIAL_RESTOCK_ID.NO_RESTOCK,
      text: t("No restock"),
      value: SPECIAL_RESTOCK_ID.NO_RESTOCK,
    });

    for (const loc of locationOptions.filter((loc) => loc.isExternal)) {
      options.push({
        type: "text" as const,
        id: loc.id,
        text: loc.name,
        value: loc.id,
      });
    }

    return options;
  }, [locationOptions, t]);

  const selectedRestockItem = useMemo(() => {
    // For redo managed locations, always use "No restock"
    if (isRedoManaged) {
      return restockDropdownOptions.find(
        (opt) => opt.id === SPECIAL_RESTOCK_ID.NO_RESTOCK,
      );
    }

    if (!restockExternalLocationId) {
      return undefined;
    }

    return restockDropdownOptions.find(
      (opt) => opt.id === restockExternalLocationId,
    );
  }, [restockDropdownOptions, restockExternalLocationId, isRedoManaged]);

  const handleLocationDropdownSelect = async (item: RedoListItem<string>) => {
    if (item.id === ADD_NEW_LOCATION_ID) {
      setModalOpen(true);
      return;
    }

    const locationId = item.value;

    if (externalLocationMap.has(locationId)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const addressId = externalLocationMap.get(locationId)!;
      const locationInfo = locationOptions.find((loc) => loc.id === locationId);
      onLocationSelect(
        addressId,
        locationInfo?.isExternal ? locationId : undefined,
      );
      return;
    }

    const locationInfo = locationOptions.find((loc) => loc.id === locationId);

    if (locationInfo?.isExternal && locationInfo.provider) {
      if (!team) {
        return;
      }

      const result = await rpcClient.getOrCreateExternalAddress({
        provider: locationInfo.provider,
        externalId: locationId,
      });

      setExternalLocationMap((prev) =>
        new Map(prev).set(locationId, result.addressId),
      );

      onLocationSelect(result.addressId, locationId);
    } else {
      onLocationSelect(locationId, undefined);
    }
  };

  const handleSaveCustomLocation = async (
    location: Step.LocationWithAddress,
  ) => {
    if (location.locationId) {
      onLocationSelect(location.locationId);
    }
  };

  const handleRestockDropdownSelect = (item: RedoListItem<string>) => {
    onRestockLocationSelect(addressId, item.value);
  };

  return (
    <div className={styles.locationCard}>
      <Flex dir="column" gap="md">
        <Flex align="center" dir="row" justify="space-between">
          <Text fontSize="sm" fontWeight="semibold">
            {cardTitle ?? t("Location {{number}}", { number: index + 1 })}
          </Text>
          <RedoButton
            aria-label={t("Delete")}
            hierarchy="tertiary"
            IconLeading={TrashIcon}
            onClick={() => onDelete(addressId)}
            size="xs"
            theme="destructive"
          />
        </Flex>

        <RedoSingleSelectDropdownInput
          description={
            addressId === SPECIAL_LOCATION_ID.ORIGINAL
              ? t(
                  "Uses the {{platformName}} location ID on the fulfillment to look up the address configured in {{platformName}}.",
                  { platformName: "Shopify" },
                )
              : addressId === SPECIAL_LOCATION_ID.SHIPMENT_ORIGIN
                ? t(
                    "Uses the fulfillment's origin address directly. Recommended when an ERP or 3rd-party system manages fulfillments and maps a single {{platformName}} location to multiple physical locations.",
                    { platformName: "Shopify" },
                  )
                : undefined
          }
          fitToAnchor={false}
          label={t("Return location")}
          options={locationDropdownOptions}
          optionSelected={({ item }) => handleLocationDropdownSelect(item)}
          placeholder={t("Select location...")}
          selectedItem={selectedLocationItem}
          size="xs"
        />

        {isShopify && (
          <Flex dir="column" gap="xs">
            <RedoSingleSelectDropdownInput
              disabled={isRedoManaged}
              label={t("Restocks to")}
              options={restockDropdownOptions}
              optionSelected={({ item }) => handleRestockDropdownSelect(item)}
              placeholder={t("Select location...")}
              selectedItem={selectedRestockItem}
              size="xs"
              state={!restockExternalLocationId ? "error" : "default"}
            />
          </Flex>
        )}
      </Flex>

      <AddLocationModal
        initialLocation={null}
        onSave={handleSaveCustomLocation}
        open={modalOpen}
        setOpen={setModalOpen}
      />
    </div>
  );
});
